import { useEffect, useRef } from 'react';
import { posthog } from '@src/lib/posthog';
import type { User } from '@src/types/user';

/**
 * Identifies the authenticated user in PostHog once per session.
 *
 * Note: The User type does not carry partner-nesting fields
 * (e.g. _partneruser_of_user). Partner group identification is skipped
 * here; wire it up separately if the API ever returns that data.
 * The `is_partner_user` flag is included as a trait so PostHog can
 * still segment partner vs. direct users.
 */
export default function usePostHogIdentify(user: User | null | undefined): void {
    const identifiedRef = useRef<number | null>(null);

    useEffect(() => {
        if (!user || !user.id) return;
        if (identifiedRef.current === user.id) return;
        identifiedRef.current = user.id;

        posthog.identify(String(user.id), {
            email: user.user_email,
            name: [user.user_first_name, user.user_last_name].filter(Boolean).join(' '),
            get_started_step: user.get_started_step ?? 0,
            plan_type: user.plan_type,
            is_partner_user: user.is_partner_user,
            subscription_status: user.subscription_status,
        });
    }, [user?.id]);
}
